import * as React from 'react'
import {
    Box,
    Button,
    Chip,
    Dialog,
    IconButton,
    InputAdornment,
    MenuItem,
    Stack,
    TextField,
    Typography
} from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import PublicRoundedIcon from '@mui/icons-material/PublicRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import SmartphoneRoundedIcon from '@mui/icons-material/SmartphoneRounded'

import useAutoTranslate from '../hooks/useAutoTranslate'

function FieldLabel({ children, required = false }) {
    return (
        <Typography variant="body1" sx={{ fontWeight: 700 }}>
            {children}
            {required && (
                <Box component="span" sx={{ color: '#ff6b6b', ml: 0.5 }}>
                    *
                </Box>
            )}
        </Typography>
    )
}

function FieldHint({ children }) {
    return (
        <Stack direction="row" spacing={0.75} alignItems="flex-start">
            <InfoOutlinedIcon sx={{ mt: '2px', fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.45 }}>
                {children}
            </Typography>
        </Stack>
    )
}

function PickerFilterButton({ active, icon, label, count, onClick }) {
    return (
        <Button
            onClick={onClick}
            startIcon={icon}
            sx={{
                minWidth: 0,
                px: 1.35,
                py: 0.82,
                borderRadius: 1.8,
                border: '1px solid rgba(255,255,255,0.12)',
                backgroundColor: active ? 'rgba(101, 117, 145, 0.42)' : 'rgba(255,255,255,0.035)',
                color: 'text.primary',
                textTransform: 'none',
                fontWeight: 700,
                '&:hover': {
                    backgroundColor: active ? 'rgba(101, 117, 145, 0.5)' : 'rgba(255,255,255,0.075)'
                }
            }}
        >
            <Box component="span">{label}</Box>
            <Box
                component="span"
                sx={{
                    ml: 1,
                    px: 0.72,
                    minWidth: 28,
                    height: 26,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 1.25,
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    color: 'text.secondary',
                    fontSize: '0.84rem',
                    fontWeight: 800
                }}
            >
                {count}
            </Box>
        </Button>
    )
}

function getPlatformAccent(platform) {
    return platform === 'e4k' ? {
        borderColor: 'rgba(24, 230, 123, 0.75)',
        backgroundColor: 'rgba(24, 230, 123, 0.12)',
        color: '#12eb83'
    } : {
        borderColor: 'rgba(63, 138, 255, 0.75)',
        backgroundColor: 'rgba(63, 138, 255, 0.12)',
        color: '#4ea8ff'
    }
}

function ServerTypeBadge({ platform, label }) {
    const accent = getPlatformAccent(platform)

    return (
        <Box
            component="span"
            sx={{
                px: 0.95,
                height: 26,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 1.4,
                border: `1px solid ${accent.borderColor}`,
                backgroundColor: accent.backgroundColor,
                color: accent.color,
                fontSize: '0.82rem',
                fontWeight: 700
            }}
        >
            {label}
        </Box>
    )
}

const TEMP_TARGET_OPTIONS = [
    { value: '', label: 'Disabled' },
    { value: 'battle_for_the_throne', label: 'BTH' },
    { value: 'outer_realms', label: 'OR' },
    { value: 'auto', label: 'OR/BTH' }
]

const PICKER_FILTERS = Object.freeze({
    all: 'all',
    gge: 'gge',
    e4k: 'e4k'
})

function ServerPickerDialog({
    open,
    selectedOption,
    serverOptions,
    appLanguage = 'en',
    onClose,
    onSelect
}) {
    const [searchQuery, setSearchQuery] = React.useState('')
    const [activeFilter, setActiveFilter] = React.useState(PICKER_FILTERS.all)
    const t = useAutoTranslate(appLanguage, [
        'Server Picker',
        'Search by name or code...',
        'Clear',
        'All Servers',
        'Empire',
        'E4K',
        'Click to select a server',
        'No servers match your search',
        'server',
        'servers'
    ])

    React.useEffect(() => {
        if (!open)
            return

        setSearchQuery('')
        setActiveFilter(PICKER_FILTERS.all)
    }, [open])

    const filterCounts = React.useMemo(() => ({
        all: serverOptions.length,
        gge: serverOptions.filter(option => option.platform === 'gge').length,
        e4k: serverOptions.filter(option => option.platform === 'e4k').length
    }), [serverOptions])

    const filteredOptions = React.useMemo(() => {
        const normalizedQuery = searchQuery.trim().toLowerCase()

        return serverOptions.filter(option => {
            if (activeFilter !== PICKER_FILTERS.all && option.platform !== activeFilter)
                return false

            if (!normalizedQuery)
                return true

            const searchSource = [
                option.name,
                option.code,
                option.label,
                option.networkLabel,
                option.serverHost,
                option.instanceLocaId
            ].join(' ').toLowerCase()

            return searchSource.includes(normalizedQuery)
        })
    }, [activeFilter, searchQuery, serverOptions])

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth={false}
            BackdropProps={{
                sx: {
                    backgroundColor: 'rgba(4, 9, 18, 0.7)',
                    backdropFilter: 'blur(10px)'
                }
            }}
            PaperProps={{
                sx: {
                    width: 'min(92vw, 560px)',
                    maxWidth: '560px',
                    borderRadius: 3,
                    background: 'linear-gradient(180deg, rgba(36,49,76,0.98) 0%, rgba(29,42,67,0.98) 100%)',
                    border: '1px solid rgba(125, 150, 190, 0.2)',
                    boxShadow: '0 24px 80px rgba(0,0,0,0.5)',
                    overflow: 'hidden'
                }
            }}
        >
            <Box sx={{ px: 3.25, py: 2.75 }}>
                <Stack spacing={2.2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                        <Stack direction="row" spacing={1.1} alignItems="center">
                            <PublicRoundedIcon sx={{ color: 'text.primary', fontSize: 28 }} />
                            <Typography variant="h4" sx={{ fontSize: '1.9rem', fontWeight: 800 }}>
                                {t('Server Picker')}
                            </Typography>
                        </Stack>
                        <IconButton onClick={onClose} sx={{ color: 'text.secondary' }}>
                            <CloseRoundedIcon />
                        </IconButton>
                    </Stack>

                    <Stack direction="row" spacing={1.1}>
                        <TextField
                            autoFocus
                            fullWidth
                            size="small"
                            value={searchQuery}
                            onChange={event => setSearchQuery(event.target.value)}
                            placeholder={t('Search by name or code...')}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 1.8
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchRoundedIcon sx={{ color: 'text.secondary' }} />
                                    </InputAdornment>
                                )
                            }}
                        />
                        <Button
                            onClick={() => setSearchQuery('')}
                            sx={{
                                px: 2.2,
                                borderRadius: 1.8,
                                minWidth: 86,
                                backgroundColor: '#ef343d',
                                color: '#fff',
                                fontWeight: 700,
                                textTransform: 'none',
                                '&:hover': {
                                    backgroundColor: '#ff4650'
                                }
                            }}
                        >
                            {t('Clear')}
                        </Button>
                    </Stack>

                    <Stack direction="row" spacing={1.1} flexWrap="wrap" useFlexGap>
                        <PickerFilterButton
                            active={activeFilter === PICKER_FILTERS.all}
                            icon={<PublicRoundedIcon />}
                            label={t('All Servers')}
                            count={filterCounts.all}
                            onClick={() => setActiveFilter(PICKER_FILTERS.all)}
                        />
                        <PickerFilterButton
                            active={activeFilter === PICKER_FILTERS.gge}
                            icon={<PublicRoundedIcon />}
                            label={t('Empire')}
                            count={filterCounts.gge}
                            onClick={() => setActiveFilter(PICKER_FILTERS.gge)}
                        />
                        <PickerFilterButton
                            active={activeFilter === PICKER_FILTERS.e4k}
                            icon={<SmartphoneRoundedIcon />}
                            label={t('E4K')}
                            count={filterCounts.e4k}
                            onClick={() => setActiveFilter(PICKER_FILTERS.e4k)}
                        />
                    </Stack>

                    <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        {`${filteredOptions.length} ${t(filteredOptions.length === 1 ? 'server' : 'servers')}`}
                    </Typography>

                    <Box
                        sx={{
                            borderRadius: 2.3,
                            border: '1px solid rgba(255,255,255,0.1)',
                            backgroundColor: 'rgba(255,255,255,0.015)',
                            overflow: 'hidden'
                        }}
                    >
                        <Box
                            sx={{
                                maxHeight: 420,
                                overflowY: 'auto',
                                px: 1,
                                py: 1,
                                '&::-webkit-scrollbar': {
                                    width: 10
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: 'rgba(255,255,255,0.4)',
                                    borderRadius: 10
                                }
                            }}
                        >
                            <Stack spacing={0.5}>
                                {filteredOptions.length > 0 ? filteredOptions.map(option => {
                                    const isSelected = Boolean(selectedOption) &&
                                        Number(selectedOption.id) === Number(option.id) &&
                                        selectedOption.platform === option.platform

                                    return (
                                        <Box
                                            key={`${option.platform}:${option.id}`}
                                            onClick={() => onSelect?.(option)}
                                            sx={{
                                                px: 1.8,
                                                py: 1.5,
                                                borderRadius: 2,
                                                border: isSelected ? '1px solid rgba(67,128,255,0.5)' : '1px solid transparent',
                                                backgroundColor: isSelected ? 'rgba(67,128,255,0.08)' : 'transparent',
                                                cursor: 'pointer',
                                                transition: 'background-color 0.18s ease, border-color 0.18s ease',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255,255,255,0.05)'
                                                }
                                            }}
                                        >
                                            <Stack spacing={0.85}>
                                                <Stack direction="row" spacing={0.85} alignItems="center" flexWrap="wrap" useFlexGap>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: option.platform === 'e4k' ? '#7fb5ff' : '#54a7ff',
                                                            fontSize: '0.98rem',
                                                            fontWeight: 700
                                                        }}
                                                    >
                                                        {option.code}
                                                    </Typography>
                                                    <ServerTypeBadge platform={option.platform} label={t(option.networkLabel)} />
                                                </Stack>
                                                <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 800 }}>
                                                    {option.name}
                                                </Typography>
                                            </Stack>
                                        </Box>
                                    )
                                }) : (
                                    <Box
                                        sx={{
                                            px: 2,
                                            py: 4,
                                            textAlign: 'center',
                                            color: 'text.secondary'
                                        }}
                                    >
                                        <Typography variant="body1">{t('No servers match your search')}</Typography>
                                    </Box>
                                )}
                            </Stack>
                        </Box>
                    </Box>

                    <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                        {t('Click to select a server')}
                    </Typography>
                </Stack>
            </Box>
        </Dialog>
    )
}

export default function ServerSettingsPanel({
    server,
    serverOptions,
    platform,
    externalEventType,
    onServerChange,
    onPlatformChange,
    onExternalEventTypeChange,
    appLanguage = 'en'
}) {
    const [pickerOpen, setPickerOpen] = React.useState(false)
    const t = useAutoTranslate(appLanguage, [
        'Main Server',
        'Temp Target Server',
        'Primary game server',
        'Temporary target server for specific modules',
        'Empire',
        'E4K',
        'Disabled',
        'BTH',
        'OR',
        'OR/BTH',
        'Open server picker',
        'Choose a server',
        'Pick a server'
    ])

    const fallbackOption = React.useMemo(() => {
        const normalizedId = Number(server)

        if (!Number.isFinite(normalizedId))
            return null

        const networkLabel = platform === 'e4k' ? 'E4K' : 'Empire'
        const code = platform === 'e4k' ? `E4K_${normalizedId}` : `SRV${normalizedId}`
        const name = `Server ${normalizedId}`

        return {
            id: normalizedId,
            platform,
            networkLabel,
            name,
            code,
            label: `${name} (${code})`,
            serverHost: '',
            instanceLocaId: ''
        }
    }, [platform, server])

    const availableServerOptions = React.useMemo(() => {
        const nextOptions = Array.isArray(serverOptions) ? serverOptions.filter(Boolean) : []

        if (!fallbackOption)
            return nextOptions

        const hasSelectedOption = nextOptions.some(option =>
            Number(option.id) === Number(fallbackOption.id) &&
            option.platform === fallbackOption.platform
        )

        return hasSelectedOption ? nextOptions : [...nextOptions, fallbackOption]
    }, [fallbackOption, serverOptions])

    const selectedServerOption = React.useMemo(() =>
        availableServerOptions.find(option =>
            Number(option.id) === Number(server) &&
            option.platform === platform
        ) ?? fallbackOption,
    [availableServerOptions, fallbackOption, platform, server])

    const handleOpenPicker = () => {
        setPickerOpen(true)
    }

    const handleSelectServer = option => {
        onPlatformChange?.(option.platform)
        onServerChange?.(option.id)
        setPickerOpen(false)
    }

    return (
        <Stack spacing={2.2} sx={{ pt: 0.25 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.2}>
                <Stack spacing={0.75} sx={{ flex: 1 }}>
                    <FieldLabel required>{t('Main Server')}</FieldLabel>
                    <TextField
                        fullWidth
                        size="small"
                        value={selectedServerOption?.label ?? ''}
                        onClick={handleOpenPicker}
                        placeholder={t('Pick a server')}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 1.8,
                                cursor: 'pointer'
                            },
                            '& .MuiInputBase-input': {
                                cursor: 'pointer'
                            }
                        }}
                        InputProps={{
                            readOnly: true,
                            endAdornment: (
                                <InputAdornment position="end" sx={{ mr: -1.15 }}>
                                    <IconButton
                                        edge="end"
                                        onClick={event => {
                                            event.preventDefault()
                                            event.stopPropagation()
                                            handleOpenPicker()
                                        }}
                                        aria-label={t('Open server picker')}
                                        sx={{
                                            width: 44,
                                            height: 40,
                                            borderRadius: 1.5,
                                            border: '1px solid rgba(255,255,255,0.14)',
                                            color: 'text.primary'
                                        }}
                                    >
                                        <SearchRoundedIcon />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                    <Chip
                        label={t(selectedServerOption?.networkLabel ?? (platform === 'e4k' ? 'E4K' : 'Empire'))}
                        size="small"
                        sx={{
                            width: 'fit-content',
                            borderRadius: 1.5,
                            border: `1px solid ${getPlatformAccent(platform).borderColor}`,
                            backgroundColor: getPlatformAccent(platform).backgroundColor,
                            color: getPlatformAccent(platform).color,
                            fontWeight: 700
                        }}
                    />
                    <FieldHint>{t('Primary game server')}</FieldHint>
                </Stack>

                <Stack spacing={0.75} sx={{ width: { xs: '100%', md: 360 } }}>
                    <FieldLabel required>{t('Temp Target Server')}</FieldLabel>
                    <TextField
                        select
                        fullWidth
                        size="small"
                        value={externalEventType ?? ''}
                        onChange={event => onExternalEventTypeChange?.(event.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 1.8
                            }
                        }}
                    >
                        {TEMP_TARGET_OPTIONS.map(option => (
                            <MenuItem key={option.value || 'disabled'} value={option.value}>
                                {t(option.label)}
                            </MenuItem>
                        ))}
                    </TextField>
                    <FieldHint>{t('Temporary target server for specific modules')}</FieldHint>
                </Stack>
            </Stack>

            <ServerPickerDialog
                open={pickerOpen}
                selectedOption={selectedServerOption}
                serverOptions={availableServerOptions}
                appLanguage={appLanguage}
                onClose={() => setPickerOpen(false)}
                onSelect={handleSelectServer}
            />
        </Stack>
    )
}
